<template>
  <section class="mx-auto px-4 py-8 grid grid-cols-1 gap-8 lg:grid-cols-12 w-full">
    <!-- LEFT: hoofdafbeelding + thumbnails -->
    <div class="lg:col-span-7">
      <div class="relative overflow-hidden bg-white shadow-sm border border-black group cursor-zoom-in" @click="openLightbox(currentIndex)">
        <img :src="primaryImage" :alt="material.name || material.title" class="aspect-[4/3] w-full object-contain bg-[#ffffff]"/>
        <!-- views rechtsboven -->

        <div class="pointer-events-none absolute top-10 right-10 flex items-center gap-1.5 text-white drop-shadow-[0_1px_1px_rgba(0,0,0,0.9)]">
          <svg xmlns="http://www.w3.org/2000/svg" width="20.571" height="16" viewBox="0 0 20.571 16">
            <path id="eye-sharp-regular" d="M4.611,43.764A13.956,13.956,0,0,1,1.875,40a13.8,13.8,0,0,1,2.736-3.764,8.158,8.158,0,0,1,5.675-2.521,8.158,8.158,0,0,1,5.675,2.521A13.856,13.856,0,0,1,18.7,40a13.8,13.8,0,0,1-2.736,3.764,8.158,8.158,0,0,1-5.675,2.521A8.158,8.158,0,0,1,4.611,43.764ZM10.286,48c5.657,0,9.214-5.332,10.286-8-1.071-2.668-4.629-8-10.286-8S1.071,37.332,0,40C1.071,42.668,4.629,48,10.286,48Zm0-5.143A2.856,2.856,0,0,1,7.429,40a2.9,2.9,0,0,1,.054-.561l2.8.561-.561-2.8a2.9,2.9,0,0,1,.561-.054,2.857,2.857,0,0,1,0,5.714ZM5.714,40a4.571,4.571,0,1,0,4.571-4.571,4.362,4.362,0,0,0-.9.089,4.578,4.578,0,0,0-3.582,3.561l0,.025a4.624,4.624,0,0,0-.089.9Z" transform="translate(0 -32)" fill="#fff"/>
          </svg>
          <span class="text-sm font-semibold">{{ material.views ?? 0 }}</span>
        </div>

        <!-- Type badge -->
        <div class="absolute top-10 left-10 z-20 flex flex-col gap-2 items-start">
          <span v-if="isRequested" class="bg-black text-white px-3 py-1 text-[11px] font-black uppercase italic tracking-wider rounded-full">
            {{ t('productManage.filter.requested') || 'Gevraagd' }}
          </span>
          <span v-else class="bg-[#E7D7BF] text-black px-3 py-1 text-[11px] font-black uppercase italic tracking-wider rounded-full">
            {{ t('productManage.filter.offered') || 'Aangeboden' }}
          </span>
        </div>

        <!-- Diagonaal label voor reeds geclaimde producten -->
        <div v-if="claimed" class="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 -rotate-12 z-20 w-[140%] text-center">
          <span class="inline-block bg-[#E24C3B] text-white px-6 py-2 text-lg sm:text-xl font-black uppercase tracking-widest shadow-lg select-none">
            {{ (t('materials.tooLate') || 'Te laat').toUpperCase() }}
          </span>
        </div>

      </div>
      <ul v-if="images.length" class="mt-3 grid grid-cols-5 gap-2">
        <li v-for="(src, i) in images" :key="i">
          <button type="button" class="block aspect-[4/3] w-full overflow-hidden bg-white" @click="currentIndex = i">
            <img :src="src" :alt="`${material.name || material.title} ${i+1}`" class="h-full w-full object-cover"/>
          </button>
        </li>
      </ul>

    </div>


    <!-- RIGHT: titel, beschrijving, eigenschappen, CTA, aanbieder -->
    <div class="lg:col-span-5 border border-black  p-5 pb-0">
      <header class="mb-6">
        <div class="flex items-baseline justify-between gap-4">
          <h1 class="!text-[22px] font-semibold leading-snug ">{{ material.title || material.name }}</h1>
        </div>
        <p :class="['mt-2 text-neutral-700 break-words', (!showAll && needsClamp) ? 'clamp-2' : '']">
          {{ material.excerpt || material.description || '' }}
        </p>
        <button v-if="needsClamp" type="button" @click="showAll = !showAll" class="mt-1 text-sm font-medium text-mm-orange underline decoration-mm-orange underline-offset-2 outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus-visible:ring-0 active:outline-none">
          {{ showAll ? t('materials.readLess') : t('materials.readMore') }}
        </button>
      </header>



      <h3 class="font-semibold !text-[22px] capitalize ">{{ t('materials.properties') }}</h3>
      <!-- Eigenschappen -->
      <div>
        <dl class="text-[18px]">
          <div class="grid grid-cols-[auto_1fr] items-center gap-x-1 py-3 border-b-[0.5px] !border-[#eedcc5]">
            <dt class="text-neutral-600 text-left min-w-[165px]" >{{ t('regionDeals.modal.adId') }}</dt>
            <dd class="font-medium text-left "><span>{{ padId(material.id) }}</span></dd>
          </div>
          <div class="grid grid-cols-[auto_1fr] items-center gap-x-1 py-3 border-b-[0.5px] !border-[#eedcc5]">
            <dt class="text-neutral-600 text-left min-w-[165px]">{{ t('materials.category') }}</dt>
            <dd class="font-medium text-left"><a :href="material.categoryUrl || '#'" class="underline text-mm-orange decoration-mm-orange underline-offset-2">{{ material.category || material.categoryName }}</a></dd>
          </div>
          <div v-if="!isRequested && Number(material.value ?? material.valueEUR) !== 0" class="grid grid-cols-[auto_1fr] items-center gap-x-1 py-3 border-b-[0.5px] !border-[#eedcc5]">
            <dt class="text-neutral-600 text-left min-w-[165px]">{{ t('materials.value') }}</dt>
            <dd class="font-medium text-left">
              <button type="button" class="underline decoration-mm-orange text-mm-orange underline-offset-2 cursor-pointer text-left" @click="openValueInfo = true">
                € {{ euro(material.value ?? material.valueEUR) }}
              </button>
            </dd>
          </div>
          <div class="grid grid-cols-[auto_1fr] items-center gap-x-1 py-3 border-b-[0.5px] !border-[#eedcc5]">
            <dt class="text-neutral-600 text-left min-w-[165px]">{{ t('materials.dimensions') }}</dt>
            <dd class="font-medium text-left">{{ material.dimensions || material.dimensionsText || '—' }}</dd>
          </div>
          <div class="grid grid-cols-[auto_1fr] items-center gap-x-1 py-3 border-b-[0.5px] !border-[#eedcc5]">
            <dt class="text-neutral-600 text-left min-w-[165px]">{{ t('materials.weight') }}</dt>
            <dd class="font-medium text-left">{{ material.weight || material.weightText || '—' }}</dd>
          </div>
          <div class="grid grid-cols-[auto_1fr] items-center gap-x-1 py-3 border-b-[0.5px] !border-[#eedcc5]">
            <dt class="text-neutral-600 text-left min-w-[165px]">{{ t('materials.condition') }}</dt>
            <dd class="font-medium text-left capitalize">{{ getConditionLabel(material.mm_conditie) }}</dd>
          </div>
          <div class="grid grid-cols-[auto_1fr] items-center gap-x-1 py-3 border-b-[0.5px] !border-[#eedcc5]">
            <dt class="text-neutral-600 text-left min-w-[165px]">{{ t('materials.location') }}</dt>
            <dd class="font-medium text-left"><a :href="material.locationSearchUrl || '#'" class="underline text-mm-orange decoration-mm-orange underline-offset-2">{{ material.location || material.locationText }}</a></dd>
          </div>
          <div v-if="material.mm_author_role || material.author_role" class="grid grid-cols-[auto_1fr] items-center gap-x-1 py-3 border-b-[0.5px] !border-[#eedcc5]">
            <dt class="text-neutral-600 text-left min-w-[165px]">{{ t('materials.providerType') || 'Type aanbieder' }}</dt>
            <dd class="font-medium text-left capitalize">{{ isFromSchool ? (t('materials.fromSchool') || 'School') : (t('materials.fromCompany') || 'Bedrijf') }}</dd>
          </div>
          <div class="grid grid-cols-[auto_1fr] items-center gap-x-1 py-3 border-b-[0.5px] !border-[#eedcc5]">
            <dt class="text-neutral-600 text-left min-w-[165px]">{{ t('productManage.table.type') || t('materials.type') || 'Type' }}</dt>
            <dd class="font-medium text-left capitalize">
              <span v-if="isRequested" class="bg-black text-white px-3 py-1 text-[11px] font-black uppercase italic tracking-wider rounded-full">
                {{ t('productManage.filter.requested') || 'Gevraagd' }}
              </span>
              <span v-else class="bg-[#E7D7BF] text-black px-3 py-1 text-[11px] font-black uppercase italic tracking-wider rounded-full">
                {{ t('productManage.filter.offered') || 'Aangeboden' }}
              </span>
            </dd>
          </div>
        </dl>
      </div>

      <!-- CTA + Aanbieder blok: geen wit tonen wanneer uitgelogd -->
      <div
          class="mt-2 sm:-mx-5"
          :class="isLoggedIn ? 'bg-white pt-[15px] pb-10' : 'bg-transparent p-0'"
      >
        <div class="px-4 sm:px-6">
          <!-- UITGELOGD: Oranje vlak op de plek van het aanbiederblok, met kop en omschrijvingen + knoppen -->
          <div v-if="!isLoggedIn" class=" -mx-4 sm:-mx-6 rounded-none bg-gradient-to-br from-[#EA6730] to-[#F7941F] text-white p-5 sm:p-6">
            <h3 class=" !text-[32px]  font-black italic uppercase mb-4 leading-tight text-white">
              <span class="whitespace-pre-line">{{ t('marketing.pitchTitle') }}</span>
            </h3>
            <ul class="space-y-1 text-[20px]">
              <li class="flex items-start gap-1">
                <svg class="w-5 h-5 flex-shrink-0 mt-0.5" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M4 12.5l6 6L20 6" fill="none" stroke="#ffffff" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <span>{{ t('marketing.usp1') }}</span>
              </li>
              <li class="flex items-start gap-1">
                <svg class="w-5 h-5 flex-shrink-0 mt-0.5" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M4 12.5l6 6L20 6" fill="none" stroke="#ffffff" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <span>{{ t('marketing.usp2') }}</span>
              </li>
              <li class="flex items-start gap-1">
                <svg class="w-5 h-5 flex-shrink-0 mt-0.5" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M4 12.5l6 6L20 6" fill="none" stroke="#ffffff" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <span>{{ t('marketing.usp3') }}</span>
              </li>
              <li class="flex items-start gap-1">
                <svg class="w-5 h-5 flex-shrink-0 mt-0.5" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M4 12.5l6 6L20 6" fill="none" stroke="#ffffff" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <span>{{ t('marketing.usp4') }}</span>
              </li>
            </ul>
            <div class="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <a href="/inloggen" class="w-full sm:w-auto bg-black text-white italic uppercase font-extrabold min-h-12 px-6 py-3 inline-flex items-center justify-center text-center leading-tight whitespace-normal">
                {{ t('buttons.login') }}
              </a>
              <a href="/registreren" class="w-full sm:w-auto bg-white text-black italic uppercase font-extrabold min-h-12 px-6 py-3 inline-flex items-center justify-center text-center leading-tight whitespace-normal">
                {{ t('titles.register') }}
              </a>
            </div>
          </div>

          <!-- Wel ingelogd: claimknop zolang niet geclaimd en mag claimen -->
          <div v-if="isLoggedIn && isClaimable && !claimed" class="mb-4">
            <button
                type="button"
                @click="openClaimConfirm = true"
                class="block w-full !bg-[#458b45] px-8 py-5 text-center text-[22px] font-bold italic uppercase !text-white transition-all min-h-16 tracking-widest leading-tight !font-['futura-pt-condensed',_sans-serif]"
            >
              {{ isRequested ? (t('materials.deliverCta') || 'Ik wil dit leveren') : (t('materials.claimCta') || 'Ik wil dit claimen') }}
            </button>
          </div>
          <!-- Geleverd door eigen school: status completed en huidige gebruiker is de claimer -->
          <div v-if="isLoggedIn && isOwnCompletedClaim" class="mb-4 text-center">
            <div class="inline-block -rotate-12 mb-8 mt-4">
              <span class="bg-[#458b45] px-4 py-2 text-xl font-bold uppercase tracking-widest text-white !font-['futura-pt-condensed',_sans-serif] italic">{{ isRequested ? 'Geleverd' : 'Geleverd' }}</span>
            </div>
            <p class="text-[20px] text-[#458b45] font-semibold">{{ isRequested ? 'Geleverd - Jij hebt dit al geleverd.' : 'Geleverd - Jij hebt dit materiaal al ontvangen.' }}</p>
          </div>
          <!-- Eigen actieve claim: bedrijf/school heeft zelf gereageerd maar nog niet completed -->
          <div v-if="isLoggedIn && isOwnActiveClaim && !isOwnCompletedClaim" class="mb-4 text-center">
            <div class="inline-block -rotate-12 mb-8 mt-4">
              <span class="bg-[#458b45] px-4 py-2 text-xl font-bold uppercase tracking-widest text-white !font-['futura-pt-condensed',_sans-serif] italic">{{ isRequested ? 'Aangemeld' : 'Geclaimd' }}</span>
            </div>
          </div>
          <!-- Alleen ingelogd tonen dat het geclaimd is (en niet geannuleerd, niet eigen claim) -->
          <div v-if="isLoggedIn && claimed && !isOwnActiveClaim && !isOwnCompletedClaim" class="mb-4 text-center">
            <div class="inline-block -rotate-12 mb-8 mt-4">
              <span class="bg-[#E24C3B] px-4 py-2 text-xl font-bold uppercase tracking-widest text-white !font-['futura-pt-condensed',_sans-serif] italic">{{ isRequested ? 'Voorzien' : 'Geclaimd' }}</span>
            </div>
            <p class="text-[20px] text-[#E24C3B] font-semibold">{{ isRequested ? 'Voorzien - Dit materiaal gaat al geleverd worden.' : 'Dit materiaal is helaas al geclaimd door iemand anders.' }}</p>
          </div>

          <!-- Aanbieder blok tonen in het witte vlak voor ingelogde gebruikers (behalve bij geclaimd conform wens) -->
          <div v-if="isLoggedIn && !claimed && material.provider" class="mt-4">
            <h2 class="mt-0 mb-4 !text-[32px] font-bold italic uppercase leading-none text-black !font-['futura-pt-condensed',_sans-serif]">{{ t('materials.provider') }}</h2>
            <div class="flex items-center gap-3">
              <div class="grid h-16 w-16 place-items-center overflow-hidden rounded-full bg-[#ee6b3f] text-2xl font-bold text-white leading-none">
                <img v-if="material.provider.companyLogoUrl" :src="material.provider.companyLogoUrl" alt="Logo" class="h-16 w-16 object-contain bg-white"/>
                <img v-else-if="material.provider.avatarUrl" :src="material.provider.avatarUrl" alt="" class="h-16 w-16 object-cover"/>
                <span v-else class="flex items-center justify-center h-full w-full">{{ material.provider.initials || initials(material.provider.name) }}</span>
              </div>
              <div class="min-w-0 leading-none">
                <div class="truncate font-semibold leading-tight text-lg">{{ material.provider.name }}</div>
                <a v-if="material.provider.website" :href="material.provider.website" target="_blank" rel="noopener noreferrer" class="truncate text-sm text-black underline decoration-black underline-offset-2">{{ host(material.provider.website) }}</a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- Waarde toelichting modal -->
  <MmModal v-model="openValueInfo" :title="t('materials.valueInfoTitle') || 'Waarde toelichting'">
    <p>
      {{ t('materials.valueInfoBody') || 'De materialen worden GRATIS aangeboden. We vermelden alleen dit bedrag om aan te geven voor welke waarde het vertegenwoordigt en voor het indienen van cofinanciering.' }}
    </p>
  </MmModal>

  <!-- Claim bevestigen modal -->
  <MmModal v-model="openClaimConfirm" :title="isRequested ? 'Bevestig jouw levering' : 'Bevestig jouw claim'">
    <div class="space-y-4">
      <p v-if="isRequested">
        Je wilt op onderstaande vraag reageren. Bevestig dat je dit kunt leveren. De vragende partij neemt daarna contact met jou op om de verdere afhandeling te bespreken.
      </p>
      <p v-else>
        Je wilt onderstaand materiaal claimen. Bevestig dat dit klopt en dan is het materiaal definitief voor jou. De aanbieder neemt daarna contact met jou op om een afhaallocatie en moment af te spreken.
      </p>
      <div class="flex gap-4 items-start">
        <img :src="primaryImage" :alt="material.title || material.name" class="w-28 h-28 object-cover border border-neutral-300 bg-[#f9f4ec]"/>
        <div class="flex-1 min-w-0">
          <h3 class="font-semibold text-neutral-900 truncate">{{ material.title || material.name }}</h3>
          <p class="text-sm text-neutral-700 line-clamp-4">{{ material.excerpt || material.description }}</p>
        </div>
      </div>
    </div>
    <div v-if="claimError" class="mt-3 p-3 bg-red-50 border border-red-300 text-red-700 text-sm rounded">
      {{ claimError }}
    </div>
    <template #footer>
      <button type="button" class="mm-modal__button" @click="openClaimConfirm = false; claimError = ''" :disabled="claimLoading">Annuleren</button>
      <button type="button" @click="confirmClaim" :disabled="claimLoading" class="px-8 py-4 min-h-16 inline-flex items-center justify-center text-center leading-tight whitespace-normal italic font-bold uppercase !text-white tracking-widest !font-['futura-pt-condensed',_sans-serif] transition-all" :class="claimLoading ? '!bg-[#458b45]/70 cursor-wait' : '!bg-[#458b45]'">
        {{ claimLoading ? 'Bezig…' : (isRequested ? 'LEVERING BEVESTIGEN' : 'CLAIM BEVESTIGEN') }}
      </button>
    </template>
  </MmModal>

  <!-- Claim succes modal -->
  <MmModal v-model="openClaimSuccess" :title="isRequested ? 'Bedankt!' : 'Gefeliciteerd!'">
    <div class="space-y-4">
      <p v-if="isRequested">
        {{ t('materials.deliverSuccessBody') || 'Jouw aanbod om te leveren is verzonden! Hoe nu verder?' }}
      </p>
      <p v-else>
        {{ t('materials.claimSuccessBody') || 'Dit materiaal is nu van jou! Hoe nu verder?' }}
      </p>
      <div v-if="claimNumber" class="p-3 border border-neutral-300 bg-white text-sm">
        <strong>{{ isRequested ? 'Leveringsnummer:' : 'Claimnummer:' }}</strong>
        <span>{{ claimNumber }}</span>
      </div>
      <ol class="list-decimal pl-5 space-y-2">
        <li v-if="isRequested">
          {{ t('materials.deliverSuccessStep1') }}
        </li>
        <li v-else>
          {{ t('materials.claimSuccessStep1') }}
        </li>
        <li>{{ isRequested ? t('materials.deliverSuccessStep2') : t('materials.claimSuccessStep2') }}</li>
      </ol>
    </div>
  </MmModal>

  <!-- Lightbox Modal -->
  <MmModal v-model="lightboxOpen" :title="material.title || material.name" max-width="900px">
    <div class="relative flex items-center justify-center bg-black/5 min-h-[50vh]">
      <img :src="images[lightboxIndex]" class="max-h-[80vh] w-auto object-contain shadow-lg" />

      <!-- Navigatie (indien meer dan 1 foto) -->
      <template v-if="images.length > 1">
        <button
            @click="prevImage"
            class="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white p-2 rounded-full shadow-md transition-colors"
            aria-label="Vorige"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <button
            @click="nextImage"
            class="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white p-2 rounded-full shadow-md transition-colors"
            aria-label="Volgende"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </template>
    </div>
    <div v-if="images.length > 1" class="mt-4 text-center text-sm text-neutral-500">
      {{ lightboxIndex + 1 }} / {{ images.length }}
    </div>
  </MmModal>
</template>

<script setup>
import { computed, ref, onMounted } from 'vue'
import WpApiClient from '../lib/wp-api'
import { t as i18nT } from '../i18n'
import MmModal from './MmModal.vue'
import { padId as padIdUtil, decodeHTML } from '../lib/format-utils'

const props = defineProps({
  material: { type: Object, default: () => ({}) },
  isLoggedIn: { type: Boolean, default: false },
  currentUser: { type: Object, default: null },
})

// Local state: prefer API-loaded data over server-provided props
const loaded = ref(null)
const loading = ref(false)
const error = ref(null)

const material = computed(() => {
  const m = loaded.value || props.material || {}
  return {
    ...m,
    title: decodeHTML(m.title || m.name || ''),
    description: decodeHTML(m.description || m.content?.rendered || m.content || ''),
    excerpt: decodeHTML(m.excerpt?.rendered || m.excerpt || ''),
    category: decodeHTML(m.category || m.categoryName || '')
  }
})

// Modal state voor waarde-toelichting
const openValueInfo = ref(false)
// Claim flow state
const openClaimConfirm = ref(false)
const openClaimSuccess = ref(false)
const claimLoading = ref(false)
const claimError = ref('')
const claimed = ref(false)
const claimNumber = ref('')
const user = ref(null)
const isClaimable = computed(() => {
  if (claimed.value) return false
  const u = user.value || props.currentUser
  if (!u) return false

  const currentUserId = Number(u.id || u.ID || 0)
  const role = String(u.activeRole || u.roleType || u.mm_role_type || '').toLowerCase().trim()
  const roles = Array.isArray(u.roles) ? u.roles.map(r => String(r).toLowerCase().trim()) : []
  // Alleen een school mag claimen, maar niet als het eigen materiaal is.
  // De actieve rol (activeRole/roleType) heeft prioriteit; bij coordinator mag nooit geclaimd worden,
  // ook niet als de gebruiker ook een school- of bedrijfsrol heeft.
  const isCoordinatorRole = role === 'coordinator'
  const isSchool = !isCoordinatorRole && (role === 'school' || roles.includes('school') || roles.some(r => r.includes('school')))
  const isCompany = !isCoordinatorRole && (role === 'bedrijf' || role === 'company' || roles.includes('bedrijf') || roles.includes('company') || roles.some(r => r.includes('bedrijf') || r.includes('company')))

  // Bij gevraagd materiaal mag een bedrijf reageren; bij aangeboden materiaal mag een school claimen.
  // Alleen de juiste rol mag claimen; coordinators en andere rollen mogen niet claimen.
  const materialType = String(material.value?.mm_type || material.value?.type || '').toLowerCase()
  const isMaterialRequested = materialType === 'gevraagd' || materialType === 'requested'
  const isCorrectRole = isMaterialRequested ? isCompany : isSchool
  const isCorrectRoleFallback = isCorrectRole

  // Extra check voor post_status: alleen 'publish' (actief) of 'private' is claimbaar
  const status = material.value?.postStatus || 'publish'
  const isClaimableStatus = status === 'publish' || status === 'private'

  const materialAuthorId = Number(material.value?.authorId || material.value?.author || material.value?.post_author || 0)

  // Als we geen auteur kunnen vinden, staan we het claimen toe (liever te veel dan te weinig)
  // tenzij we zeker weten dat het van de huidige gebruiker is.
  const isNotOwnMaterial = currentUserId === 0 || materialAuthorId === 0 || materialAuthorId !== currentUserId

  const mmDebug = typeof window !== 'undefined' && window.location.search.includes('mm_debug=1')
  if (mmDebug) {
    console.log('MmMaterialsDetail claim check:', {
      isLoggedIn: isLoggedIn.value,
      isClaimable: isCorrectRoleFallback && isNotOwnMaterial && isClaimableStatus,
      isCorrectRole,
      isCorrectRoleFallback: isCorrectRoleFallback,
      isClaimableStatus,
      isNotOwnMaterial,
      claimed: claimed.value,
      userRole: role,
      userRoles: roles,
      currentUserId,
      materialAuthorId,
      materialStatus: status,
      rawUser: u
    })
  }

  return isCorrectRoleFallback && isNotOwnMaterial && isClaimableStatus
})
const isLoggedIn = computed(() => props.isLoggedIn || !!user.value || !!props.currentUser)

const isFromSchool = computed(() => {
  const role = String(material.value?.mm_author_role || material.value?.author_role || '').toLowerCase()
  return role === 'school'
})
const isFromCompany = computed(() => {
  const role = String(material.value?.mm_author_role || material.value?.author_role || '').toLowerCase()
  return role === 'bedrijf' || role === 'company'
})
const isOwnActiveClaim = computed(() => {
  if (!claimed.value) return false
  // Gebruik server-side is_own_claim vlag (meest betrouwbaar, ook als claimed_by_user = -1)
  if (material.value?.isOwnClaim) return true
  // Fallback: vergelijk user IDs als claimed_by_user wel zichtbaar is
  const u = user.value || props.currentUser
  if (!u) return false
  const currentUserId = Number(u.id || u.ID || 0)
  const claimedByUserId = Number(material.value?.claimedByUserId || 0)
  return currentUserId > 0 && claimedByUserId > 0 && currentUserId === claimedByUserId
})
const isOwnCompletedClaim = computed(() => {
  if (!claimed.value) return false
  const claimStatus = material.value?.claimStatus || ''
  if (claimStatus !== 'completed') return false
  // Gebruik server-side is_own_claim vlag (meest betrouwbaar)
  if (material.value?.isOwnClaim) return true
  // Fallback: vergelijk user IDs als claimed_by_user wel zichtbaar is
  const u = user.value || props.currentUser
  if (!u) return false
  const currentUserId = Number(u.id || u.ID || 0)
  const claimedByUserId = Number(material.value?.claimedByUserId || 0)
  return currentUserId > 0 && claimedByUserId > 0 && currentUserId === claimedByUserId
})
const isRequested = computed(() => {
  const type = String(material.value?.mm_type || material.value?.type || '').toLowerCase()
  return type === 'gevraagd' || type === 'requested'
})

// Lightbox state
const lightboxOpen = ref(false)
const lightboxIndex = ref(0)
const openLightbox = (index) => {
  lightboxIndex.value = index
  lightboxOpen.value = true
}
const nextImage = () => {
  lightboxIndex.value = (lightboxIndex.value + 1) % images.value.length
}
const prevImage = () => {
  lightboxIndex.value = (lightboxIndex.value - 1 + images.value.length) % images.value.length
}

const t = (key) => i18nT(key)

const currentIndex = ref(0)
const images = computed(() => material.value?.images || [])
const fallback = computed(() => 'data:image/svg+xml;utf8,' + encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 600 450"><rect width="600" height="450" fill="#f3f4f6"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="#9ca3af" font-family="sans-serif" font-size="14">${i18nT('materials.noImage')}</text></svg>`))
const primaryImage = computed(() => images.value[currentIndex.value] || fallback.value)

const showAll = ref(false)
const needsClamp = computed(() => (material.value?.description || '').length > 180)

function euro(val) {
  if (val == null || isNaN(Number(val))) return '—'
  return Number(val).toFixed(2)
}
function padId(val){ return padIdUtil(val) }
function getStatusLabel(status) {
  if (!status) return '—'
  const key = `productCreate.statusOptions.${status}`
  const label = i18nT(key)
  return label !== key ? label : status
}
function getConditionLabel(condition) {
  if (!condition) return '—'
  const key = `productCreate.conditionOptions.${condition}`
  const label = i18nT(key)
  return label !== key ? label : condition
}
function initials(name) {
  if (!name || typeof name !== 'string') return ''
  try {
    return name.split(/\s+/).filter(Boolean).slice(0, 2).map(p => p[0]).join('').toUpperCase()
  } catch (e) {
    return ''
  }
}
function host(url) {
  if (!url) return ''
  try { return new URL(url).host } catch { return String(url) }
}

function stripTags(html = '') {
  const tmp = document.createElement('div')
  tmp.innerHTML = String(html)
  return tmp.textContent || tmp.innerText || ''
}

function dedupe(arr = []) { return Array.from(new Set(arr.filter(Boolean))) }

function parseSlugFromPath() {
  const parts = (window.location.pathname || '').split('/').filter(Boolean)
  // Neem laatste segment (verwacht: {slug}-{id} of {slug})
  const last = parts.length ? parts[parts.length - 1] : ''
  // Strip trailing -digits om zuivere slug te krijgen
  return String(last).replace(/-(\d+)$/, '')
}

function mapMaterial(item, mediaList = []) {
  if (!item || typeof item !== 'object') return {}
  const embedded = item._embedded || {}
  const featured = Array.isArray(embedded['wp:featuredmedia']) ? embedded['wp:featuredmedia'][0] : null
  const featuredUrl = featured?.source_url || null
  const author = Array.isArray(embedded.author) ? embedded.author[0] : null

  // Terms: find materiaal_categorie in embedded terms
  let cat = null
  const termsGroups = Array.isArray(embedded['wp:term']) ? embedded['wp:term'] : []
  termsGroups.forEach(group => {
    group.forEach(t => {
      if (t && (t.taxonomy === 'materiaal_categorie' || t.slug /* fallback */)) {
        if (!cat) cat = t
      }
    })
  })

  const value = item.meta?.mm_waarde_eur ?? item.mm_waarde_eur ?? null
  const locationText = item.meta?.mm_locatie ?? item.mm_locatie ?? null
  const dimensionsText = item.meta?.mm_afmetingen ?? item.mm_afmetingen ?? null
  const weightText = item.meta?.mm_gewicht ?? item.mm_gewicht ?? null
  const mm_conditie = item.meta?.mm_conditie ?? item.mm_conditie ?? item.eigenschappen?.mm_conditie ?? item.eigenschappen?.conditie ?? item.conditie ?? null

  const postStatus = String(item.status || '').toLowerCase()
  let postStatusLabel = i18nT(`productCreate.statusOptions.${postStatus}`)
  if (postStatusLabel === `productCreate.statusOptions.${postStatus}`) {
    // Fallback if not in i18n
    if (postStatus === 'publish') postStatusLabel = 'Actief'
    else if (postStatus === 'draft' || postStatus === 'pending') postStatusLabel = 'Concept'
    else if (postStatus === 'trash') postStatusLabel = 'Verwijderd'
    else postStatusLabel = postStatus
  }

  const claimStatus = String(item.mm_claim?.status || '').toLowerCase()
  if (claimStatus === 'claimed') postStatusLabel = 'Geclaimd'
  else if (claimStatus === 'collected') postStatusLabel = 'Afgehaald'

  // Build gallery images: featured + attached media
  const gallery = []
  if (featuredUrl) {
    gallery.push(featuredUrl)
  }

  mediaList.forEach(m => {
    const url = m?.source_url || m?.media_details?.sizes?.large?.source_url
    if (!url) return

    // Voorkom dubbelen: als de URL al in de lijst staat of als de featuredUrl (zonder query params/sizes) overeenkomt
    const isDuplicate = gallery.some(existingUrl => {
      // Simpele vergelijking
      if (existingUrl === url) return true
      // Vergelijking op basis van bestandsnaam (vrij veilig voor WP uploads)
      const getFileName = (u) => {
        if (!u || typeof u !== 'string') return ''
        return u.split('/').pop().split('?')[0].split('-').slice(0, -1).join('-') || u.split('/').pop().split('?')[0]
      }
      // Eigenlijk is het beter om gewoon te kijken of de basenaam hetzelfde is
      const baseExisting = existingUrl && typeof existingUrl === 'string' ? existingUrl.split('/').pop().split('?')[0] : ''
      const baseNew = url && typeof url === 'string' ? url.split('/').pop().split('?')[0] : ''
      return baseExisting === baseNew
    })

    if (!isDuplicate) {
      gallery.push(url)
    }
  })

  const authorNameFallback = item.author_name || null
  const authorWebsiteFallback = item.author_website || null
  const authorLogoFallback = item.author_company_logo_url || null

  const providerName = author?.mm_org_name || author?.name || authorNameFallback || author?.slug || null
  const providerWebsite = author?.url || author?.website || authorWebsiteFallback || null
  const providerLogo = author?.mm_company_logo_url || authorLogoFallback || null

  const mm_author_role = item.mm_author_role ?? item.author_role ?? author?.mm_author_role ?? author?.author_role ?? null
  const mm_type = item.mm_type ?? item.type ?? item.meta?.mm_type ?? item.meta?.type ?? null

  const mapped = {
    id: item.id,
    slug: item.slug,
    name: stripTags(item.title?.rendered) || item.title || '',
    title: stripTags(item.title?.rendered) || item.title || '',
    description: stripTags(item.content?.rendered || item.excerpt?.rendered || ''),
    images: dedupe(gallery),
    value: value != null && value !== '' ? Number(value) : null,
    category: cat?.name || null,
    categoryUrl: cat?.link || null,
    dimensionsText: dimensionsText || null,
    weightText: weightText || null,
    status: postStatus || null,
    postStatusLabel: postStatusLabel,
    location: locationText || null,
    locationText: locationText || null,
    mm_conditie: mm_conditie,
    authorId: Number(item.author || item.post_author || (item.meta && item.meta.post_author) || 0),
    locationUrl: locationText ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(locationText)}` : null,
    provider: (providerName || providerWebsite || providerLogo) ? {
      name: providerName,
      website: providerWebsite,
      avatarUrl: (author?.avatar_urls && (author.avatar_urls['96'] || author.avatar_urls['48'] || author.avatar_urls['24'])) || null,
      companyLogoUrl: providerLogo,
    } : null,
    views: item.mm_views ?? item.meta?.mm_views ?? 0,
    postStatus: item.status || 'publish',
    claimed: hasActiveClaim(item),
    claimStatus: item?.mm_claim?.status || '',
    claimedByUserId: Number(item?.mm_claim?.claimed_by_user || 0),
    isOwnClaim: !!(item?.mm_claim?.is_own_claim),
    locationSearchUrl: locationText ? `/materialen?location=${encodeURIComponent(locationText)}` : null,
    mm_author_role: mm_author_role,
    mm_type: mm_type,
  }
  return mapped
}

function hasActiveClaim(item) {
  const claim = item?.mm_claim || null
  return !!(claim && Number(claim.claimed_by_user || 0) !== 0 && String(claim.status || '').toLowerCase() !== 'cancelled')
}

onMounted(async () => {
  try {
    const slug = parseSlugFromPath()
    if (!slug) return
    loading.value = true
    const api = new WpApiClient()
    const item = await api.getMaterialBySlug(slug)
    if (!item) {
      console.warn('Material not found for slug:', slug)
      loading.value = false
      return
    }
    const media = await api.getMediaByParent(item.id).catch(() => [])
    loaded.value = mapMaterial(item, media)
    // Reset carousel index when new images arrive
    currentIndex.value = 0
    // Initialize claimed UI state from REST
    try {
      claimed.value = !!loaded.value.claimed
      if (window.MM_DEBUG || window.materiaalAjax?.debug) {
        console.log('MmMaterialsDetail loaded material:', {
          id: loaded.value.id,
          status: loaded.value.postStatus,
          claimed: claimed.value,
          authorId: loaded.value.authorId
        })
      }
    } catch(_) {}
    // Fetch current user to gate claim button
    try {
      let userRes = await api.getCurrentUser({ nonce: window?.materiaalAjax?.nonce })

      // Automatische retry bij nonce error
      if (userRes && !userRes.ok && (userRes.error === 'invalid_nonce' || (userRes.error && userRes.error.includes('nonce')))) {
        const newNonce = await api.refreshNonce(false);
        if (newNonce) {
          userRes = await api.getCurrentUser({ nonce: newNonce });
        }
      }

      const ok = !!(userRes && (userRes.ok || userRes.success))
      const u = userRes && (userRes.user || (userRes.data && userRes.data.user) || userRes.currentUser)
      if (ok && u) {
        user.value = u
        if (window.MM_DEBUG || window.materiaalAjax?.debug) {
          console.log('MmMaterialsDetail user loaded:', u)
        }
      } else if (window.MM_DEBUG || window.materiaalAjax?.debug) {
        console.log('MmMaterialsDetail user load failed or not logged in:', userRes)
      }
    } catch (_) { /* no-op */ }
  } catch (e) {
    error.value = e
  } finally {
    loading.value = false
  }
})

async function confirmClaim() {
  if (!material.value?.id) return
  claimLoading.value = true
  try {
    const api = new WpApiClient()
    const res = await api.claimMaterial(material.value.id, { nonce: window?.materiaalAjax?.nonce })
    if (res && (res.ok || res.success)) {
      claimed.value = true
      try { claimNumber.value = String(res.claimNumber || (res.data && res.data.claimNumber) || '') } catch(_) {}
      claimError.value = ''
      openClaimConfirm.value = false
      openClaimSuccess.value = true
    } else {
      const msg = res && (res.error || res.message)
      claimError.value = msg || (isRequested.value ? t('materials.deliverError') : t('materials.claimError'))
    }
  } catch (e) {
    console.error('Claim mislukt', e)
    claimError.value = isRequested.value ? t('materials.deliverError') : t('materials.claimError')
  } finally {
    claimLoading.value = false
  }
}
</script>

<style scoped>
.line-clamp-4 { display: -webkit-box; -webkit-line-clamp: 4; -webkit-box-orient: vertical; overflow: hidden; }
.clamp-2{ display:-webkit-box; -webkit-line-clamp:2; -webkit-box-orient:vertical; overflow:hidden; }
</style>
